/* Basicariche v2.0 — theme.js
   Mobile nav toggle + tiny event bus + thumb gallery + utilities.

   To enable verbose logging in the console, run:
     window.BC_DEBUG = true
   then reload the page. */

(() => {
  'use strict';

  // ---- Tiny pub/sub for cart updates etc. ----
  const listeners = new Map();
  window.bcEvents = {
    on(event, cb) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event).add(cb);
      if (window.BC_DEBUG) console.log('[bc] on', event);
      return () => listeners.get(event).delete(cb);
    },
    emit(event, payload) {
      const set = listeners.get(event);
      if (window.BC_DEBUG) console.log('[bc] emit', event, '→', set ? set.size + ' listeners' : '0 listeners');
      if (!set) return;
      for (const cb of set) cb(payload);
    },
  };

  // ---- Mobile nav ----
  document.querySelectorAll('[data-bc-mobile-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;
      const open = target.hasAttribute('hidden');
      if (open) {
        target.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
      } else {
        target.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  });

  // ---- Thumb gallery (PDP) ----
  const mainImg = document.querySelector('[data-bc-pdp-main]');
  if (mainImg) {
    document.querySelectorAll('[data-bc-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.bcThumbSrc;
        if (src) mainImg.src = src;
        document.querySelectorAll('[data-bc-thumb]').forEach((t) => t.classList.remove('bc-pdp__thumb--active'));
        thumb.classList.add('bc-pdp__thumb--active');
      });
    });
  }

  // ---- <bc-quantity-input> ----
  if (!customElements.get('bc-quantity-input')) {
    customElements.define(
      'bc-quantity-input',
      class extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('[data-bc-qty-input]');
          if (!this.input) return;
          this.querySelectorAll('[data-bc-qty]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const dir = btn.dataset.bcQty;
              const cur = parseInt(this.input.value, 10) || 0;
              const min = parseInt(this.input.min, 10) || 0;
              const next = dir === 'increment' ? cur + 1 : Math.max(min, cur - 1);
              if (next === cur) return;
              this.input.value = next;
              this.input.dispatchEvent(new Event('change', { bubbles: true }));
            });
          });
          // Auto-submit cart updates on cart page / drawer
          this.input.addEventListener('change', () => {
            const key = this.dataset.key;
            if (key) {
              window.bcEvents.emit('cart:update-line', { key, quantity: parseInt(this.input.value, 10) || 0 });
            }
          });
        }
      }
    );
  }

  // ---- Slider + slideshow / presentation sections ----
  // The theme includes Dawn-style slideshow markup/CSS, but this custom theme did not
  // load Dawn's global.js. Register the two small custom elements needed by the
  // "Presentazione" section so arrows, dots, autoplay and editor slide selection work.
  if (!window.accessibilityStrings) window.accessibilityStrings = {};
  window.accessibilityStrings.playSlideshow = window.accessibilityStrings.playSlideshow || 'Riproduci la presentazione';
  window.accessibilityStrings.pauseSlideshow = window.accessibilityStrings.pauseSlideshow || 'Metti in pausa la presentazione';

  class BCSliderComponent extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bcSliderReady === 'true') return;
      this.dataset.bcSliderReady = 'true';

      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.currentPageElement = this.querySelector('.slider-counter--current');
      this.pageTotalElement = this.querySelector('.slider-counter--total');
      this.sliderControlLinksArray = Array.from(this.querySelectorAll('.slider-counter__link'));
      this.enableSliderLooping = this.enableSliderLooping || false;

      if (!this.slider || !this.sliderItems.length) return;

      this.onResize = this.initPages.bind(this);
      this.onScroll = this.update.bind(this);
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevButton) this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      if (this.nextButton) this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

      this.sliderControlLinksArray.forEach((link, index) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.goToSlide(index + 1);
        });
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.slider);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      this.initPages();
    }

    disconnectedCallback() {
      if (this.slider && this.onScroll) this.slider.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.autoplay) clearInterval(this.autoplay);
    }

    initPages() {
      this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]')).filter((item) => item.offsetParent !== null);
      if (!this.sliderItems.length || !this.slider) return;
      this.sliderItemOffset = this.sliderItems[1]
        ? this.sliderItems[1].offsetLeft - this.sliderItems[0].offsetLeft
        : this.sliderItems[0].clientWidth;
      if (!this.sliderItemOffset || this.sliderItemOffset < 1) this.sliderItemOffset = this.slider.clientWidth || 1;
      this.totalPages = this.sliderItems.length;
      this.update();
    }

    resetPages() {
      this.initPages();
    }

    getCurrentPage() {
      if (!this.sliderItemOffset) this.initPages();
      const page = Math.round(this.slider.scrollLeft / (this.sliderItemOffset || 1)) + 1;
      return Math.max(1, Math.min(page, this.totalPages || this.sliderItems.length || 1));
    }

    update() {
      if (!this.slider || !this.sliderItems.length) return;
      this.currentPage = this.getCurrentPage();

      if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
      if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages || this.sliderItems.length;

      this.sliderControlLinksArray.forEach((link, index) => {
        const active = index === this.currentPage - 1;
        link.classList.toggle('slider-counter__link--active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      this.setSlideVisibility();

      if (this.enableSliderLooping) {
        if (this.prevButton) this.prevButton.removeAttribute('disabled');
        if (this.nextButton) this.nextButton.removeAttribute('disabled');
        return;
      }

      if (this.prevButton) {
        if (this.currentPage <= 1) this.prevButton.setAttribute('disabled', 'disabled');
        else this.prevButton.removeAttribute('disabled');
      }
      if (this.nextButton) {
        if (this.currentPage >= (this.totalPages || this.sliderItems.length)) this.nextButton.setAttribute('disabled', 'disabled');
        else this.nextButton.removeAttribute('disabled');
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      let targetPage = (this.currentPage || this.getCurrentPage()) + direction;
      const maxPage = this.totalPages || this.sliderItems.length || 1;

      if (this.enableSliderLooping) {
        if (targetPage < 1) targetPage = maxPage;
        if (targetPage > maxPage) targetPage = 1;
      } else {
        targetPage = Math.max(1, Math.min(targetPage, maxPage));
      }

      this.goToSlide(targetPage);
    }

    goToSlide(page) {
      const index = Math.max(0, Math.min(page - 1, this.sliderItems.length - 1));
      const item = this.sliderItems[index];
      if (!item) return;
      this.setSlidePosition(item.offsetLeft);
    }

    setSlidePosition(position) {
      this.slider.scrollTo({ left: position, behavior: 'smooth' });
    }

    setSlideVisibility() {
      this.sliderItems.forEach((item, index) => {
        const isActive = index === (this.currentPage || 1) - 1;
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        item.setAttribute('tabindex', isActive ? '-1' : '-1');
        item.querySelectorAll('a, button, input, select, textarea').forEach((focusable) => {
          if (isActive) focusable.removeAttribute('tabindex');
          else focusable.setAttribute('tabindex', '-1');
        });
      });
    }
  }

  class BCSlideshowComponent extends BCSliderComponent {
    connectedCallback() {
      this.enableSliderLooping = true;
      super.connectedCallback();

      if (this.dataset.bcSlideshowReady === 'true') return;
      this.dataset.bcSlideshowReady = 'true';

      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.autoplayButtonIsSetToPlay = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplaySpeed = Math.max(3, parseInt(this.slider?.dataset.speed || '5', 10)) * 1000;

      if (this.sliderAutoplayButton) {
        this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      }

      this.addEventListener('mouseenter', this.pause.bind(this));
      this.addEventListener('focusin', this.pause.bind(this));
      this.addEventListener('mouseleave', () => {
        if (this.autoplayButtonIsSetToPlay) this.play();
      });
      this.addEventListener('focusout', () => {
        if (this.autoplayButtonIsSetToPlay) this.play();
      });

      if (this.slider?.dataset.autoplay === 'true' && this.sliderItems.length > 1 && !this.reducedMotion.matches) {
        this.play();
      }
    }

    play() {
      if (!this.slider || this.sliderItems.length < 2) return;
      clearInterval(this.autoplay);
      this.slider.setAttribute('aria-live', 'off');
      this.autoplay = setInterval(() => {
        const maxPage = this.totalPages || this.sliderItems.length || 1;
        const nextPage = (this.currentPage || this.getCurrentPage()) >= maxPage ? 1 : (this.currentPage || this.getCurrentPage()) + 1;
        this.goToSlide(nextPage);
      }, this.autoplaySpeed || 5000);
    }

    pause() {
      if (this.slider) this.slider.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
    }

    autoPlayToggle() {
      if (!this.sliderAutoplayButton) return;
      if (this.autoplayButtonIsSetToPlay) {
        this.pause();
        this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
        this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
      } else {
        this.play();
        this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
        this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
      }
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }
  }

  if (!customElements.get('slider-component')) {
    customElements.define('slider-component', BCSliderComponent);
  }
  if (!customElements.get('slideshow-component')) {
    customElements.define('slideshow-component', BCSlideshowComponent);
  }

})();
